import React, { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { useRenderCount } from '../../diagnostics/useRenderCount';
import { ACCENT, FONTS } from '../theme';

/**
 * KillFeedPanel — top-right corner overlay showing the last N kills.
 *
 * Subscribes to the structured `playerDied` broadcast that
 * CombatHandler.handlePlayerDefeated emits (server-authoritative,
 * routed through broadcastTracked so sim recordings capture it).
 * NOT consuming the chat 'kill' channel — that's the chat surface;
 * killfeed has its own visual treatment + auto-fade.
 *
 * Behavior:
 *   - Last 5 entries kept, oldest dropped on overflow
 *   - Each entry auto-fades out 5s after it lands
 *   - POV-emphasized: when the local player is killer or victim, the
 *     entry is bolder + colored
 *   - Suicide / world kill (killerSid === null && killerName === victimName)
 *     renders as "Player died" instead of "X killed Y"
 *
 * Mounted from src/main.tsx alongside DeathOverlay + DuelOverlays +
 * DrainOverlay + LobbyInfoPanel.
 *
 * data-testid: 'kill-feed-panel' for future smoke tests.
 *
 * Format: "[abilityIcon] killerName  ➜  victimName"
 *   - abilityIcon resolves from emoji map (fallback ⚔️)
 *   - Self-kill: "💀 Killed themselves"
 *   - Bot kill (killerName starts with 'Bot '): same shape, just the
 *     bot's display name
 */

const MAX_ENTRIES = 5;
const ENTRY_FADE_MS = 5000;

interface KillEntry {
  id: number;
  victimSid: string;
  victimName: string;
  killerSid: string | null;
  killerName: string;
  abilityId: string | null;
  bornAt: number;
}

interface PlayerDiedPayload {
  victimSid: string;
  victimName: string;
  killerSid: string | null;
  killerName: string;
  abilityId?: string | null;
  timestamp?: string;
}

// Tiny emoji-by-school map. Falls back to ⚔️ for unmapped abilities so
// the killfeed always renders something. Adding more here is cheap;
// don't bother with a full emoji-per-ability table — that's a designer
// decision and would belong in the ability registry.
const ABILITY_EMOJI: Record<string, string> = {
  fireball: '🔥', meteor: '☄️', combustion: '🔥', fire_blast: '🔥',
  frost_bolt: '❄️', blizzard: '❄️', ice_barrier: '❄️',
  blink: '✨', arcane_missiles: '✨', chain_lightning: '⚡',
  mage_auto: '✨',
  warrior_auto: '⚔️', mortal_strike: '🗡️', whirlwind: '🌀', execute: '☠️',
  ranger_auto: '🏹', aimed_shot: '🎯', multiple_arrows: '🏹', explosive_shot: '💥',
  paladin_auto: '🔨', crusader_strike: '🔨', divine_storm: '✨', consecration: '🔆',
  assassin_auto: '🗡️', backstab: '🗡️', fan_of_knives: '🗡️', eviscerate: '🗡️',
};

const resolveEmoji = (abilityId: string | null): string => {
  if (!abilityId) return '⚔️';
  return ABILITY_EMOJI[abilityId] ?? '⚔️';
};

export const KillFeedPanel: React.FC = () => {
  useRenderCount('KillFeedPanel');
  const [entries, setEntries] = useState<KillEntry[]>([]);

  useColyseusMessages({
    playerDied: (msg: PlayerDiedPayload) => {
      if (!msg || !msg.victimName || !msg.killerName) return;
      const entry: KillEntry = {
        id: Date.now() + Math.random(),
        victimSid: msg.victimSid,
        victimName: msg.victimName,
        killerSid: msg.killerSid ?? null,
        killerName: msg.killerName,
        abilityId: msg.abilityId ?? null,
        bornAt: Date.now(),
      };
      setEntries(prev => [...prev, entry].slice(-MAX_ENTRIES));
    },
  });

  // Periodic prune of expired entries — only while the feed shows, and it
  // keeps the same array when nothing expired (B7: the always-on prune made
  // a new empty array 4 times a second, re-rendering the hidden panel).
  const hasEntries = entries.length > 0;
  useEffect(() => {
    if (!hasEntries) return undefined;
    const pruneInterval = setInterval(() => {
      const now = Date.now();
      setEntries(prev => {
        const kept = prev.filter(e => now - e.bornAt < ENTRY_FADE_MS);
        return kept.length === prev.length ? prev : kept;
      });
    }, 250);
    return () => clearInterval(pruneInterval);
  }, [hasEntries]);

  if (entries.length === 0) return null;

  const localSid: string | null = (window as any).nm?.getRoom?.()?.sessionId ?? null;

  return (
    <div
      data-testid="kill-feed-panel"
      style={{
        position: 'fixed',
        top: 60, // sits below DevStatusBar + LobbyInfoPanel
        right: 14,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 4,
        pointerEvents: 'none',
        zIndex: 99000,
        userSelect: 'none',
      }}
    >
      {entries.map(e => {
        const localIsKiller = !!localSid && e.killerSid === localSid;
        const localIsVictim = !!localSid && e.victimSid === localSid;
        const isPOV = localIsKiller || localIsVictim;
        const isSelfKill = e.killerSid === null && e.killerName === e.victimName;

        const age = Date.now() - e.bornAt;
        const fadeStart = ENTRY_FADE_MS - 800; // hold full opacity until last 800ms
        const opacity = age < fadeStart ? 1 : Math.max(0, 1 - (age - fadeStart) / 800);

        const bg = localIsKiller
          ? 'rgba(46, 204, 113, 0.85)'   // green when you got the kill
          : localIsVictim
            ? 'rgba(231, 76, 60, 0.85)'   // red when you got killed
            : 'rgba(20, 20, 30, 0.78)';
        // Neutral (non-POV) entries take the brass fitting; POV entries keep
        // their semantic green/red (HUD-restyle Lane B.6).
        const border = localIsKiller ? '#27ae60' : localIsVictim ? '#c0392b' : ACCENT.brass;
        const fontWeight = isPOV ? 700 : 500;

        return (
          <div
            key={e.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '4px 10px',
              background: bg,
              border: `1px solid ${border}`,
              borderRadius: 4,
              color: '#ecf0f1',
              fontSize: 12,
              fontFamily: FONTS.ui,
              fontWeight,
              opacity,
              transition: 'opacity 0.2s',
              minWidth: 200,
              maxWidth: 360,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {isSelfKill ? (
              <>
                <span>💀</span>
                <span>{e.victimName}</span>
                <span style={{ opacity: 0.7 }}>died</span>
              </>
            ) : (
              <>
                <span>{resolveEmoji(e.abilityId)}</span>
                <span style={{ flexShrink: 0 }}>{e.killerName}</span>
                <span style={{ opacity: 0.7 }}>➜</span>
                <span style={{ flexShrink: 0 }}>{e.victimName}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
